/* eslint-disable @typescript-eslint/no-magic-numbers */
import {Browser} from '@clappr/core'

export {default} from './template'
export {default as classNames} from './styles.scss'

export const options = {
  allowUserInteraction: false
}

const LARGE_WIDTH_BREAKPOINT = 570
const LARGE_HEIGHT_BREAK_POINT = 320

const VERTICAL_PADDINGS_SUM = 2 * 30
const LARGE_TITLE_MIN_HEIGHT = 20 + 5 + 1 * 30 + VERTICAL_PADDINGS_SUM
const LARGE_TITLE_MAX_HEIGHT = 20 + 5 + 2 * 30 + VERTICAL_PADDINGS_SUM
const SMALL_TITLE_MAX_HEIGHT = 20 + 5 + 2 * 25 + VERTICAL_PADDINGS_SUM

const getWatchNextHeight = (isLarge: boolean, linesCount: number): number => {
  const previewHeight = isLarge ? 105 : 75
  if (linesCount < 1) return previewHeight

  const verticalTitleGaps = isLarge ? 10 + 15 : 10 + 10
  const titleLineHeight = isLarge ? 20 : 15

  return previewHeight + verticalTitleGaps + linesCount * titleLineHeight
}

export const getContainerClassNames = ({
  width,
  height,
  suggestHeight,
  docking,
  title,
  likes,
  episodes
}: {
  width: number
  height: number
  suggestHeight: number | null
  docking: boolean
  title: boolean
  likes: boolean
  episodes: boolean
  // eslint-disable-next-line sonarjs/cognitive-complexity
}): [string, boolean][] => {
  const availableHeight =
    suggestHeight == null ? height : height - suggestHeight

  const tinySuggest = suggestHeight != null && height < 210
  const fullHeightSuggest = suggestHeight != null && availableHeight < 120

  const playerHeight = fullHeightSuggest ? height : availableHeight

  const largeContainerWidth = width >= LARGE_WIDTH_BREAKPOINT
  const largeContainerHeight = height >= LARGE_HEIGHT_BREAK_POINT
  const largePlayerHeight = playerHeight >= LARGE_HEIGHT_BREAK_POINT

  let largeTitle

  if (suggestHeight != null && !fullHeightSuggest) {
    largeTitle =
      Browser.isMobile || docking
        ? largeContainerWidth && LARGE_TITLE_MAX_HEIGHT <= availableHeight
        : largeContainerWidth && LARGE_TITLE_MIN_HEIGHT <= availableHeight
  } else {
    largeTitle = largeContainerWidth && largeContainerHeight
  }

  const titleMaxHeight = largeTitle
    ? LARGE_TITLE_MAX_HEIGHT
    : SMALL_TITLE_MAX_HEIGHT
  const shortenTitle = playerHeight < titleMaxHeight

  /* WATCH NEXT */
  const shiftableHeight = playerHeight - (0 + 2 * 15 + 5)
  const activeTopShift = title || likes ? 35 : 0
  const activeShiftableHeight = playerHeight - (activeTopShift + 2 * 15 + 35)

  const watchNextLarge = largeContainerWidth && largeContainerHeight
  const watchNextVariants = [3, 2, 1].map(
    (linesCount) =>
      [linesCount, getWatchNextHeight(watchNextLarge, linesCount)] as const
  )

  const [watchNextVariant = 0] =
    watchNextVariants.find(
      ([_, watchNextHeight]) => shiftableHeight >= watchNextHeight
    ) ?? []
  const [activeWatchNextVariant = 0] =
    watchNextVariants.find(
      ([_, watchNextHeight]) => activeShiftableHeight >= watchNextHeight
    ) ?? []

  const watchNextHidden =
    activeShiftableHeight < getWatchNextHeight(watchNextLarge, 0)

  /* THUMBNAILS */
  // BOTTOM + BOTTOM PADDING + (LABEL HEIGHT + LABEL TOP MARGIN) + IMAGE HEIGHT + TOP PADDING + TOP
  const hideThumbnails =
    availableHeight <
    35 + 12 + (episodes ? 15 + 5 : 0) + 29 + 15 + (title ? 35 : 0)

  // BOTTOM + BOTTOM PADDING + TWO LINES LABEL HEIGHT + LABEL TOP MARGIN + IMAGE HEIGHT + TOP PADDING + TOP
  const episodesWithTwoLines =
    largeContainerWidth &&
    availableHeight >= 35 + 12 + 2 * 15 + 5 + 60 + 15 + (title ? 35 : 0)

  const fullscreenSettingsMenu =
    Browser.isMobile || !largeContainerWidth || !largeContainerHeight

  return [
    ['eplayer-fullscreen-menu', fullscreenSettingsMenu],
    ['eplayer-normal-menu', !fullscreenSettingsMenu],
    ['eplayer-tiny-suggest', tinySuggest],
    ['eplayer-full-height-suggest', fullHeightSuggest],
    ['eplayer-shorten-title', shortenTitle],
    ['eplayer-large-title', largeTitle],
    ['eplayer-large-screen', largeContainerWidth && largePlayerHeight],
    ['eplayer-large-size', largeContainerWidth && largeContainerHeight],
    ['eplayer-large-height', largePlayerHeight],
    ['eplayer-watch-next-hidden', watchNextHidden],
    ['eplayer-watch-next-large', watchNextLarge],
    ['eplayer-watch-next-one-line', watchNextVariant === 1],
    ['eplayer-watch-next-two-lines', watchNextVariant === 2],
    ['eplayer-watch-next-three-lines', watchNextVariant === 3],
    ['eplayer-active-watch-next-hidden-text', activeWatchNextVariant === 0],
    ['eplayer-active-watch-next-one-line', activeWatchNextVariant === 1],
    ['eplayer-active-watch-next-two-lines', activeWatchNextVariant === 2],
    ['eplayer-active-watch-next-three-lines', activeWatchNextVariant === 3],
    ['eplayer-hide-thumbnails', hideThumbnails],
    ['eplayer-episodes-one-line', episodes && !episodesWithTwoLines],
    ['eplayer-episodes-two-lines', episodes && episodesWithTwoLines]
  ]
}
